// src/pages/MarkBestSellers.jsx
import { useState, useEffect } from "react";

export default function MarkBestSellers() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetch("/api/products/")
      .then((res) => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setProducts(list);
        // Auto-select current best sellers
        const bestSellerIds = list.filter(p => p.is_best_seller).map(p => p.id);
        setSelected(bestSellerIds);
      })
      .catch(() => alert("Failed to load products"))
      .finally(() => setLoading(false));
  }, []);

  const toggleProduct = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelected([]);
    } else {
      setSelected(products.map((p) => p.id));
    }
    setSelectAll(!selectAll);
  };

  const markAsBestSeller = async () => {
    if (selected.length === 0) {
      return alert("Please select at least one product!");
    }

    const promises = selected.map((id) =>
      fetch(`/api/products/${id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_best_seller: true }),
      })
    );

    try {
      await Promise.all(promises);
      alert(`${selected.length} product${selected.length > 1 ? 's' : ''} marked as BEST SELLER!`);
    } catch (err) {
      alert("Failed to update. Check console.");
      console.error(err);
    }
  };

  const clearBestSellers = async () => {
    if (!window.confirm("Remove Best Seller tag from ALL products?")) return;

    const bestSellers = products.filter(p => p.is_best_seller);
    const promises = bestSellers.map((p) =>
      fetch(`/api/products/${p.id}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_best_seller: false }),
      })
    );

    try {
      await Promise.all(promises);
      alert("All Best Seller tags cleared!");
      window.location.reload();
    } catch (err) {
      alert("Failed to clear best sellers");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 font-['Roboto'] text-gray-800">
      {/* HEADER */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-4xl font-medium text-gray-900">
                Mark Best Sellers
              </h1>
              <p className="text-lg text-gray-600 mt-2">
                Choose which products to feature on the homepage
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={toggleSelectAll}
                className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl font-medium transition"
              >
                {selectAll ? "Unselect All" : "Select All"}
              </button>

              <button
                onClick={markAsBestSeller}
                disabled={selected.length === 0}
                className="px-8 py-3 bg-gradient-to-r from-black to-gray-800 hover:from-gray-900 hover:to-black text-white rounded-xl font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                MARK {selected.length} AS BEST SELLER
              </button>

              <button
                onClick={clearBestSellers}
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* PRODUCTS GRID */}
      <div className="max-w-7xl mx-auto px-6 py-10">
        {loading ? (
          <div className="text-center py-32">
            <p className="text-2xl text-gray-600">Loading products...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-32">
            <p className="text-2xl text-gray-500">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-8">
            {products.map((p) => {
              const firstVariant = p.variants?.[0];
              const totalStock = p.variants?.reduce((sum, v) => sum + (v.stock || 0), 0) || 0;
              const variantCount = p.variants?.length || 0;

              return (
                <div
                  key={p.id}
                  className={`relative bg-white rounded-2xl shadow-md overflow-hidden border-2 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                    selected.includes(p.id)
                      ? "border-black ring-4 ring-black ring-opacity-10"
                      : "border-gray-200"
                  } ${p.is_best_seller ? "ring-4 ring-yellow-400 ring-opacity-50" : ""}`}
                >
                  {/* BEST SELLER BADGE */}
                  {p.is_best_seller && (
                    <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20">
                      <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-sm font-bold px-6 py-2 rounded-full shadow-lg">
                        BEST SELLER
                      </span>
                    </div>
                  )}

                  {/* IMAGE */}
                  <div className="relative h-80 bg-gray-50">
                    {firstVariant?.image_url ? (
                      <img
                        src={firstVariant.image_url}
                        alt={p.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = "https://placehold.co/400x500/e5e7eb/9ca3a3a3?text=No+Image";
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg font-medium">
                        No Image
                      </div>
                    )}
                  </div>

                  {/* INFO */}
                  <div className="p-6">
                    <h3 className="font-semibold text-lg text-gray-900 line-clamp-2">
                      {p.name}
                    </h3>

                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-3xl font-bold text-gray-900">
                        ₹{p.offer_price || p.price}
                      </p>
                      {p.offer_price && (
                        <span className="text-lg text-gray-500 line-through">
                          ₹{p.price}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Stock:</span>
                        <span className="font-bold text-gray-900 ml-2">{totalStock}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Variants:</span>
                        <span className="font-bold text-gray-900 ml-2">{variantCount}</span>
                      </div>
                    </div>
                  </div>

                  {/* SELECTION CHECKBOX */}
                  <div className="absolute top-4 right-4 z-30">
                    <button
                      onClick={() => toggleProduct(p.id)}
                      className={`w-10 h-10 rounded-xl border-3 flex items-center justify-center transition-all shadow-lg ${
                        selected.includes(p.id)
                          ? "bg-black border-black"
                          : "bg-white border-gray-300 hover:border-gray-500"
                      }`}
                    >
                      {selected.includes(p.id) && (
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}